import React, { useState, useMemo } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    LinearProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Tooltip,
    InputAdornment,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import axios from 'axios';
import { apiString as API_URL } from '../../utils/constants';

// Casing matches Backend DTOs (PascalCase)
export interface AiRateLimitConfigDto {
    Id?: string;
    Scope: 'Global' | 'Role' | 'Provider';
    TargetId: string | null;
    TargetName: string | null;
    MaxRequestsPerMinute: number;
    MaxTokensPerMinute: number;
    MaxTokenBudgetPerMonth: number;
    TokensUsedThisMonth?: number;
    BudgetUtilizationPercent?: number;
    EffectiveSource?: string;
}

export interface ProviderRole {
    Id: string;
    Name: string;
}

export interface ProviderPreview {
    Id: string;
    Name: string;
    Role: string;
    IsActive: boolean;
}

interface ProviderUsageInfo {
    ProviderId: string;
    TotalTokens: number;
}

interface AiRateLimitPanelProps {
    configs: AiRateLimitConfigDto[];
    roles: ProviderRole[];
    providers: ProviderPreview[];
    providerMonthlyUsage: ProviderUsageInfo[];
    onRefresh: () => void;
    setToast: (toast: { open: boolean; message: string; severity: 'success' | 'error' }) => void;
}

const AiRateLimitPanel: React.FC<AiRateLimitPanelProps> = ({
    configs,
    roles,
    providers,
    providerMonthlyUsage,
    onRefresh,
    setToast,
}) => {
    // Search states
    const [providerSearch, setProviderSearch] = useState('');
    const [roleSearch, setRoleSearch] = useState('');

    // Global Defaults Edit State
    const [isEditingGlobal, setIsEditingGlobal] = useState(false);
    const [globalForm, setGlobalForm] = useState<AiRateLimitConfigDto>({
        Scope: 'Global',
        TargetId: null,
        TargetName: null,
        MaxRequestsPerMinute: 10,
        MaxTokensPerMinute: 50000,
        MaxTokenBudgetPerMonth: 500000,
    });

    // Dialog state for Role/Provider Edit
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingConfig, setEditingConfig] = useState<AiRateLimitConfigDto | null>(null);
    const [dialogSaving, setDialogSaving] = useState(false);

    // Resolve global config
    const globalConfig = useMemo(() => {
        const found = configs.find(c => c.Scope === 'Global');
        if (found) return found;
        return {
            Scope: 'Global' as const,
            TargetId: null,
            TargetName: null,
            MaxRequestsPerMinute: 10,
            MaxTokensPerMinute: 50000,
            MaxTokenBudgetPerMonth: 500000,
        };
    }, [configs]);

    // Handle editing global config
    const handleEditGlobalClick = () => {
        setGlobalForm({ ...globalConfig });
        setIsEditingGlobal(true);
    };

    const handleSaveGlobal = async () => {
        if (globalForm.MaxRequestsPerMinute < 1) {
            setToast({
                open: true,
                message: 'Max Requests per Minute must be at least 1.',
                severity: 'error',
            });
            return;
        }
        if (globalForm.MaxTokensPerMinute < 1) {
            setToast({
                open: true,
                message: 'Max Tokens per Minute must be at least 1.',
                severity: 'error',
            });
            return;
        }
        if (globalForm.MaxTokenBudgetPerMonth < 0) {
            setToast({
                open: true,
                message: 'Monthly Token Budget cannot be negative.',
                severity: 'error',
            });
            return;
        }

        try {
            await axios.post(`${API_URL}/chpai/ratelimit/config`, globalForm);
            setToast({
                open: true,
                message: 'Global rate limits saved successfully.',
                severity: 'success',
            });
            setIsEditingGlobal(false);
            onRefresh();
        } catch (error: any) {
            setToast({
                open: true,
                message: error.response?.data || 'Failed to save global rate limits.',
                severity: 'error',
            });
        }
    };

    // Open dialog to edit/override role limit
    const handleOpenRoleEdit = (role: ProviderRole, config: AiRateLimitConfigDto | null) => {
        if (config) {
            setEditingConfig({ ...config });
        } else {
            setEditingConfig({
                Scope: 'Role',
                TargetId: role.Id,
                TargetName: role.Name,
                MaxRequestsPerMinute: globalConfig.MaxRequestsPerMinute,
                MaxTokensPerMinute: globalConfig.MaxTokensPerMinute,
                MaxTokenBudgetPerMonth: globalConfig.MaxTokenBudgetPerMonth,
            });
        }
        setDialogOpen(true);
    };

    // Open dialog to edit/override provider limit
    const handleOpenProviderEdit = (provider: ProviderPreview, config: AiRateLimitConfigDto | null, effectiveLimits: { rpm: number, tpm: number, budget: number }) => {
        if (config) {
            setEditingConfig({ ...config });
        } else {
            setEditingConfig({
                Scope: 'Provider',
                TargetId: provider.Id,
                TargetName: provider.Name,
                MaxRequestsPerMinute: effectiveLimits.rpm,
                MaxTokensPerMinute: effectiveLimits.tpm,
                MaxTokenBudgetPerMonth: effectiveLimits.budget,
            });
        }
        setDialogOpen(true);
    };

    const handleSaveConfigDialog = async () => {
        if (!editingConfig) return;

        if (editingConfig.MaxRequestsPerMinute < 1) {
            setToast({
                open: true,
                message: 'Max Requests per Minute must be at least 1.',
                severity: 'error',
            });
            return;
        }
        if (editingConfig.MaxTokensPerMinute < 1) {
            setToast({
                open: true,
                message: 'Max Tokens per Minute must be at least 1.',
                severity: 'error',
            });
            return;
        }
        if (editingConfig.MaxTokenBudgetPerMonth < 0) {
            setToast({
                open: true,
                message: 'Monthly Token Budget cannot be negative.',
                severity: 'error',
            });
            return;
        }

        try {
            setDialogSaving(true);
            await axios.post(`${API_URL}/chpai/ratelimit/config`, editingConfig);
            setToast({
                open: true,
                message: `Successfully saved limits for ${editingConfig.TargetName}.`,
                severity: 'success',
            });
            setDialogOpen(false);
            setEditingConfig(null);
            onRefresh();
        } catch (error: any) {
            setToast({
                open: true,
                message: error.response?.data || 'Failed to save configuration.',
                severity: 'error',
            });
        } finally {
            setDialogSaving(false);
        }
    };

    const handleDeleteConfig = async (configId: string, name: string) => {
        if (!window.confirm(`Are you sure you want to delete the override for ${name}?`)) {
            return;
        }
        try {
            await axios.delete(`${API_URL}/chpai/ratelimit/config/${configId}`);
            setToast({
                open: true,
                message: `Deleted override for ${name}. Limits reverted to higher tier.`,
                severity: 'success',
            });
            onRefresh();
        } catch (error: any) {
            setToast({
                open: true,
                message: error.response?.data || 'Failed to delete override.',
                severity: 'error',
            });
        }
    };

    // Resolve limits for each Role
    const roleLimitData = useMemo(() => {
        return roles.map(role => {
            const explicitConfig = configs.find(c => c.Scope === 'Role' && c.TargetId === role.Id);
            return {
                role,
                explicitConfig: explicitConfig || null,
                rpm: explicitConfig ? explicitConfig.MaxRequestsPerMinute : globalConfig.MaxRequestsPerMinute,
                tpm: explicitConfig ? explicitConfig.MaxTokensPerMinute : globalConfig.MaxTokensPerMinute,
                budget: explicitConfig ? explicitConfig.MaxTokenBudgetPerMonth : globalConfig.MaxTokenBudgetPerMonth,
                isOverridden: !!explicitConfig
            };
        }).filter(r => (r.role?.Name || '').toLowerCase().includes((roleSearch || '').toLowerCase()));
    }, [roles, configs, globalConfig, roleSearch]);

    // Resolve limits, usage, and source for each Provider
    const providerLimitData = useMemo(() => {
        return providers.map(provider => {
            // Find explicit provider override
            const providerConfig = configs.find(c => c.Scope === 'Provider' && c.TargetId === provider.Id);
            
            // Find role matching this provider
            const matchedRole = roles.find(r => r.Name === provider.Role);
            const roleConfig = matchedRole ? configs.find(c => c.Scope === 'Role' && c.TargetId === matchedRole.Id) : null;

            // Resolve effective values
            let rpm = globalConfig.MaxRequestsPerMinute;
            let tpm = globalConfig.MaxTokensPerMinute;
            let budget = globalConfig.MaxTokenBudgetPerMonth;
            let source = 'Global';

            if (providerConfig) {
                rpm = providerConfig.MaxRequestsPerMinute;
                tpm = providerConfig.MaxTokensPerMinute;
                budget = providerConfig.MaxTokenBudgetPerMonth;
                source = 'Provider';
            } else if (roleConfig) {
                rpm = roleConfig.MaxRequestsPerMinute;
                tpm = roleConfig.MaxTokensPerMinute;
                budget = roleConfig.MaxTokenBudgetPerMonth;
                source = `Role: ${provider.Role}`;
            }

            // Look up monthly usage from current month's usage logs summary
            const usageInfo = providerMonthlyUsage.find(u => u.ProviderId === provider.Id);
            const tokensUsedThisMonth = usageInfo ? usageInfo.TotalTokens : 0;
            const utilizationPercent = budget > 0 ? Math.min(100.0, (tokensUsedThisMonth / budget) * 100.0) : 0;

            return {
                provider,
                providerConfig: providerConfig || null,
                rpm,
                tpm,
                budget,
                source,
                tokensUsedThisMonth,
                utilizationPercent,
            };
        }).filter(p => 
            (p.provider?.Name || '').toLowerCase().includes((providerSearch || '').toLowerCase()) ||
            (p.provider?.Role || '').toLowerCase().includes((providerSearch || '').toLowerCase())
        );
    }, [providers, configs, roles, globalConfig, providerMonthlyUsage, providerSearch]);

    // Progress bar color selector
    const getProgressBarColor = (percent: number) => {
        if (percent >= 90) return 'error';
        if (percent >= 75) return 'warning';
        return 'success';
    };

    return (
        <Stack spacing={4}>
            {/* Resolution Hierarchy Infographic */}
            <Paper
                elevation={1}
                sx={{
                    p: 2.5,
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.7)',
                    backdropFilter: 'blur(4px)',
                    border: '1px dashed rgba(63, 81, 181, 0.25)',
                }}
            >
                <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight="600" color="primary" display="flex" alignItems="center" gap={0.5}>
                        <InfoOutlinedIcon fontSize="small" /> Rate Limit Precedence Hierarchy
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Limits resolve dynamically in order of specificity: <b>Provider Overrides (Most Specific)</b> &rarr; <b>Role Defaults</b> &rarr; <b>Global Defaults (Fallback)</b>. Creating a custom override at any tier takes precedence over configurations higher in the chain.
                    </Typography>
                </Stack>
            </Paper>

            {/* Global Settings */}
            <Card
                elevation={3}
                sx={{
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                        <Box>
                            <Typography variant="h6" fontWeight="600" color="textPrimary">
                                Global Defaults
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                Baseline limits applied to all users who do not have Role or Provider overrides
                            </Typography>
                        </Box>
                        {!isEditingGlobal && (
                            <Button
                                startIcon={<EditIcon />}
                                variant="outlined"
                                onClick={handleEditGlobalClick}
                                size="small"
                            >
                                Edit Defaults
                            </Button>
                        )}
                    </Stack>

                    {isEditingGlobal ? (
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <TextField
                                    label="Max Requests / Min"
                                    type="number"
                                    value={globalForm.MaxRequestsPerMinute}
                                    onChange={(e) => setGlobalForm({ ...globalForm, MaxRequestsPerMinute: parseInt(e.target.value) || 0 })}
                                    fullWidth
                                    size="small"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <TextField
                                    label="Max Tokens / Min"
                                    type="number"
                                    value={globalForm.MaxTokensPerMinute}
                                    onChange={(e) => setGlobalForm({ ...globalForm, MaxTokensPerMinute: parseInt(e.target.value) || 0 })}
                                    fullWidth
                                    size="small"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <TextField
                                    label="Monthly Token Budget"
                                    type="number"
                                    value={globalForm.MaxTokenBudgetPerMonth}
                                    onChange={(e) => setGlobalForm({ ...globalForm, MaxTokenBudgetPerMonth: parseInt(e.target.value) || 0 })}
                                    fullWidth
                                    size="small"
                                    helperText="0 = Unlimited budget"
                                />
                            </Grid>
                            <Grid size={12}>
                                <Stack direction="row" spacing={2} justifyContent="flex-end">
                                    <Button size="small" variant="text" color="inherit" onClick={() => setIsEditingGlobal(false)}>
                                        Cancel
                                    </Button>
                                    <Button size="small" variant="contained" color="primary" onClick={handleSaveGlobal}>
                                        Save
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    ) : (
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                                    <Typography variant="caption" color="textSecondary">Max Requests / Min</Typography>
                                    <Typography variant="h5" fontWeight="600" color="primary">{globalConfig.MaxRequestsPerMinute}</Typography>
                                </Paper>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                                    <Typography variant="caption" color="textSecondary">Max Tokens / Min</Typography>
                                    <Typography variant="h5" fontWeight="600" color="primary">{globalConfig.MaxTokensPerMinute.toLocaleString()}</Typography>
                                </Paper>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                                    <Typography variant="caption" color="textSecondary">Monthly Token Budget</Typography>
                                    <Typography variant="h5" fontWeight="600" color="primary">
                                        {globalConfig.MaxTokenBudgetPerMonth > 0 ? globalConfig.MaxTokenBudgetPerMonth.toLocaleString() : 'Unlimited'}
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                    )}
                </CardContent>
            </Card>

            {/* Role Defaults */}
            <Card
                elevation={3}
                sx={{
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
                        <Box>
                            <Typography variant="h6" fontWeight="600" color="textPrimary">
                                Role-based Defaults
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                Configure default limits for specific provider roles. Unconfigured roles inherit from Global
                            </Typography>
                        </Box>
                        <TextField
                            placeholder="Search roles..."
                            value={roleSearch}
                            onChange={(e) => setRoleSearch(e.target.value)}
                            size="small"
                            sx={{ width: 220 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Stack>

                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Role Name</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Requests/Min</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Tokens/Min</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Monthly Budget</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 600 }}>Status</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {roleLimitData.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            No roles found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    roleLimitData.map(({ role, explicitConfig, rpm, tpm, budget, isOverridden }) => (
                                        <TableRow key={role.Id} hover>
                                            <TableCell sx={{ fontWeight: '500' }}>{role.Name}</TableCell>
                                            <TableCell align="right" sx={{ color: isOverridden ? 'textPrimary' : 'textSecondary' }}>
                                                {rpm}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: isOverridden ? 'textPrimary' : 'textSecondary' }}>
                                                {tpm.toLocaleString()}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: isOverridden ? 'textPrimary' : 'textSecondary' }}>
                                                {budget > 0 ? budget.toLocaleString() : 'Unlimited'}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={isOverridden ? 'Overridden' : 'Inherited'}
                                                    size="small"
                                                    color={isOverridden ? 'primary' : 'default'}
                                                    variant={isOverridden ? 'filled' : 'outlined'}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleOpenRoleEdit(role, explicitConfig)}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    {explicitConfig && (
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleDeleteConfig(explicitConfig.Id!, role.Name)}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* Provider Overrides */}
            <Card
                elevation={3}
                sx={{
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
                        <Box>
                            <Typography variant="h6" fontWeight="600" color="textPrimary">
                                Provider-Specific Overrides
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                Search and customize limits for individual providers. Overrides take highest precedence
                            </Typography>
                        </Box>
                        <TextField
                            placeholder="Search providers..."
                            value={providerSearch}
                            onChange={(e) => setProviderSearch(e.target.value)}
                            size="small"
                            sx={{ width: 260 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Stack>

                    <TableContainer sx={{ maxHeight: 500 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Provider Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Active Source</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Requests/Min</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Tokens/Min</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Monthly Budget</TableCell>
                                    <TableCell sx={{ fontWeight: 600, width: '20%' }}>Monthly Budget Usage</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {providerLimitData.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            No active providers found matching search.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    providerLimitData.map(({ provider, providerConfig, rpm, tpm, budget, source, tokensUsedThisMonth, utilizationPercent }) => {
                                        const hasCustomOverride = source === 'Provider';
                                        const hasRoleOverride = source.startsWith('Role:');
                                        let chipColor: 'warning' | 'secondary' | 'default' = 'default';
                                        if (hasCustomOverride) chipColor = 'warning';
                                        else if (hasRoleOverride) chipColor = 'secondary';

                                        return (
                                            <TableRow key={provider.Id} hover>
                                                <TableCell sx={{ fontWeight: '500' }}>{provider.Name}</TableCell>
                                                <TableCell color="textSecondary">{provider.Role || '-'}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={hasCustomOverride ? 'Custom Override' : source}
                                                        size="small"
                                                        color={chipColor}
                                                        variant={hasCustomOverride || hasRoleOverride ? 'filled' : 'outlined'}
                                                    />
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: hasCustomOverride ? 'textPrimary' : 'textSecondary' }}>
                                                    {rpm}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: hasCustomOverride ? 'textPrimary' : 'textSecondary' }}>
                                                    {tpm.toLocaleString()}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: hasCustomOverride ? 'textPrimary' : 'textSecondary' }}>
                                                    {budget > 0 ? budget.toLocaleString() : 'Unlimited'}
                                                </TableCell>
                                                <TableCell>
                                                    {budget > 0 ? (
                                                        <Tooltip title={`${tokensUsedThisMonth.toLocaleString()} / ${budget.toLocaleString()} tokens (${utilizationPercent.toFixed(1)}%)`}>
                                                            <Box>
                                                                <Stack direction="row" justifyContent="space-between" mb={0.5}>
                                                                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                                                                        {utilizationPercent.toFixed(0)}%
                                                                    </Typography>
                                                                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                                                                        {tokensUsedThisMonth.toLocaleString()} used
                                                                    </Typography>
                                                                </Stack>
                                                                <LinearProgress
                                                                    variant="determinate"
                                                                    value={utilizationPercent}
                                                                    color={getProgressBarColor(utilizationPercent)}
                                                                    sx={{ borderRadius: 1, height: 6 }}
                                                                />
                                                            </Box>
                                                        </Tooltip>
                                                    ) : (
                                                        <Typography variant="caption" color="textSecondary">
                                                            {tokensUsedThisMonth.toLocaleString()} used (No Budget)
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handleOpenProviderEdit(provider, providerConfig, { rpm, tpm, budget })}
                                                        >
                                                            {hasCustomOverride ? <EditIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                                                        </IconButton>
                                                        {providerConfig && (
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleDeleteConfig(providerConfig.Id!, provider.Name)}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* Save/Edit Config Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle fontWeight="600">
                    {editingConfig?.Id ? 'Edit Configuration Override' : 'Create Configuration Override'}
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                        Setting limits for {editingConfig?.TargetName} ({editingConfig?.Scope})
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    {editingConfig && (
                        <Stack spacing={3} sx={{ mt: 1.5 }}>
                            <TextField
                                label="Max Requests / Minute"
                                type="number"
                                value={editingConfig.MaxRequestsPerMinute}
                                onChange={(e) => setEditingConfig({ ...editingConfig, MaxRequestsPerMinute: parseInt(e.target.value) || 0 })}
                                fullWidth
                                size="small"
                            />
                            <TextField
                                label="Max Tokens / Minute"
                                type="number"
                                value={editingConfig.MaxTokensPerMinute}
                                onChange={(e) => setEditingConfig({ ...editingConfig, MaxTokensPerMinute: parseInt(e.target.value) || 0 })}
                                fullWidth
                                size="small"
                            />
                            <TextField
                                label="Monthly Token Budget"
                                type="number"
                                value={editingConfig.MaxTokenBudgetPerMonth}
                                onChange={(e) => setEditingConfig({ ...editingConfig, MaxTokenBudgetPerMonth: parseInt(e.target.value) || 0 })}
                                fullWidth
                                size="small"
                                helperText="0 = Unlimited budget"
                            />
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button variant="text" color="inherit" onClick={() => setDialogOpen(false)} disabled={dialogSaving}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="primary" onClick={handleSaveConfigDialog} disabled={dialogSaving}>
                        {dialogSaving ? 'Saving...' : 'Save Override'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default AiRateLimitPanel;
